import { useNavigate } from '@tanstack/react-router';
import { useLogoutApiAuthLogoutPost } from '@/shared/api/generated/auth/auth';
import { useAuthStore } from '@/shared/store/auth';

interface UseLogoutOptions {
  /** 로그아웃 성공 후 이동할 경로 (기본값: '/login', false면 리다이렉트 안 함) */
  redirectTo?: string | false;
  /** 로그아웃 성공 시 호출되는 콜백 */
  onSuccess?: () => void;
  /** 로그아웃 실패 시 호출되는 콜백 */
  onError?: (error: unknown) => void;
}

/**
 * useLogout - 로그아웃 기능 Hook
 *
 * Orval이 생성한 API를 사용하여 로그아웃 처리 및 네비게이션을 담당합니다.
 *
 * @example
 * ```tsx
 * const { handleLogout, isPending } = useLogout();
 *
 * <button onClick={handleLogout} disabled={isPending}>
 *   {isPending ? '로그아웃 중...' : '로그아웃'}
 * </button>
 * ```
 *
 * @example
 * ```tsx
 * // 커스텀 옵션 사용
 * const { handleLogout, isPending } = useLogout({
 *   redirectTo: '/',
 *   onSuccess: () => toast.success('로그아웃 되었습니다'),
 * });
 * ```
 */
export function useLogout(options?: UseLogoutOptions) {
  const { clearUser } = useAuthStore();
  const navigate = useNavigate();
  const { mutate: logout, isPending } = useLogoutApiAuthLogoutPost({
    mutation: {
      onSuccess: () => {
        // API 성공 후 로컬 상태 클리어
        clearUser();
        // redirectTo가 false면 리다이렉트 안 함, 문자열이면 해당 경로로, undefined면 '/login'으로
        if (options?.redirectTo !== false) {
          navigate({ to: options?.redirectTo || '/login' });
        }
        options?.onSuccess?.();
      },
      onError: (error) => {
        // 에러 발생 시에도 로컬 상태는 클리어 (세션 만료 등)
        console.error('Logout error:', error);
        clearUser();
        if (options?.redirectTo !== false) {
          navigate({ to: options?.redirectTo || '/login' });
        }
        options?.onError?.(error);
      },
    },
  });

  const handleLogout = () => {
    logout();
  };

  return {
    handleLogout,
    isPending,
  };
}
